import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from '../SignatureCanvas/SignatureCanvas';
import { WButton } from '@wareclouds/wareclouds-ui';

const SignatureModal = ({
  open,
  onClose,
  onConfirm,
  title,
  instructions,
  initialSignature,
  isLoading = false,
}) => {
  const { t } = useTranslation(['button', 'description']);
  const [signature, setSignature] = useState(initialSignature || null);
  const signaturePadRef = useRef(null);

  const handleSignatureEnd = (dataURL) => {
    setSignature(dataURL);
  };

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setSignature(null);
    }
  };

  const handleConfirm = async () => {
    if (signature && onConfirm) {
      await onConfirm(signature);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (open) {
      setSignature(initialSignature || null);
    }
  }, [open, initialSignature]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="signature-modal-title"
    >
      <DialogTitle id="signature-modal-title">
        {title || t('orders.deliver.signature.title', { ns: 'description' })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {instructions ||
            t('orders.deliver.signature.instructions', { ns: 'description' })}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 2,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          {t('orders.deliver.signature.disclaimer', { ns: 'description' })}
        </Typography>
        <SignatureCanvas
          ref={signaturePadRef}
          onSignatureEnd={handleSignatureEnd}
        />
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
        <WButton
          label={t('clear', { ns: 'button' })}
          variant="text"
          onClick={handleClear}
          size="small"
          isDisabled={isLoading}
          fullWidth
          sx={{ justifyContent: 'flex-end' }}
        />
        <WButton
          label={t('confirm', { ns: 'button' })}
          variant="contained"
          onClick={handleConfirm}
          isDisabled={!signature || isLoading}
          isLoading={isLoading}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleClose}
          disabled={isLoading}
          fullWidth
          sx={{
            backgroundColor: '#e0e0e0',
            color: '#616161',
            textTransform: 'uppercase',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
            },
          }}
        >
          {t('back', { ns: 'button' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignatureModal;
